import React, { useContext } from "react";
import { SettingsContext } from "./SettingsContext.tsx";
import { SelectButton } from "primereact/selectbutton";
import { RadioButton } from "primereact/radiobutton";
import { PlaceValueDigitOptions, PlaceValueOptions } from "../Constants.ts";
import { withLabels } from "../Labels.ts";
import { showsPlaceValueDigits } from "./AdvancedSettings.ts";
import { useTranslation } from "../../Shared/i18n/useTranslation.ts";

/**
 * Which way round the question is asked, and how big the numbers are.
 *
 * Reading a number and writing it out in parts, being handed the parts and
 * assembling them, and naming what a single digit is worth are three different
 * skills that all live under "place value", so they are selectable separately
 * as well as mixed on one sheet.
 */
export function PlaceValueSettings() {
    const { settings, setSettings } = useContext(SettingsContext);
    const t = useTranslation();

    function handleQuestionChange(value: PlaceValueOptions) {
        // SelectButton hands back null when the selected option is clicked
        // again; a sheet has to ask something.
        if (!value) {
            return;
        }
        setSettings({ ...settings, PlaceValueQuestion: value });
    }

    function handleDigitsChange(event) {
        setSettings({ ...settings, PlaceValueDigits: Number(event.target.value) });
    }

    return (
        <div className="placeValueSettingsContainer">
            <div className="regroupingContainer">
                <SelectButton
                    value={settings.PlaceValueQuestion}
                    onChange={(e) => handleQuestionChange(e.value)}
                    options={withLabels(Object.values(PlaceValueOptions), settings.Language)}
                    className="select regrouping"
                    aria-label={t('settings.questionType')}
                />
            </div>
            {showsPlaceValueDigits(settings) && (
                <>
                    <div className="selectContainer">
                        <span className="settingLabel">{t('settings.numberOfDigitsLabel')}</span>
                    </div>
                    <div className="radioButtons" role="radiogroup" aria-label={t('settings.placeValueDigitsAria')}>
                        {PlaceValueDigitOptions.map((option) => (
                            <div key={option}>
                                <RadioButton
                                    inputId={`placeValueDigits-${option}`}
                                    value={option}
                                    checked={settings.PlaceValueDigits === option}
                                    onChange={handleDigitsChange}
                                />
                                <label htmlFor={`placeValueDigits-${option}`}>{option}</label>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}

export default PlaceValueSettings;
